// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the tilt input layer (principle #23). One interface, three implementations,
// all feeding the SAME pure gravity vector into the SAME physics:
//   · gyro  — DeviceOrientationEvent angles → tiltFromOrientation() (the sensor path).
//   · touch — the engine's FLOATING joystick (fallback: real game with a thumbstick).
//   · key   — arrow/WASD held state (desktop).
// The sensor is an INPUT, never a gate: a missing/denied gyro lands the player in the
// joystick game with an honest message, never a wall. Permission is asked from a tap.
// Nothing is recorded, stored, or sent — the gyro exposes only orientation angles.

import { createJoystick, type Joystick } from '@ben-gy/game-engine/joystick';
import { tiltFromOrientation, clampVector, type Orientation } from './tilt';

export type SourceKind = 'gyro' | 'touch' | 'key';

export interface TiltSource {
  readonly kind: SourceKind;
  /** Current gravity vector, each component -1..1. Cheap; call every frame. */
  tilt(): { x: number; y: number };
  /** Re-zero the neutral pose to however the device is held now (gyro only). */
  recenter(): void;
  dispose(): void;
}

/* ── gyroscope ────────────────────────────────────────────────────────────── */

interface DOEStatic {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

export function gyroExists(): boolean {
  return typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;
}

/** iOS 13+ gates the sensor behind an explicit, gesture-bound permission call. */
export function gyroNeedsPermission(): boolean {
  return (
    gyroExists() &&
    typeof (window.DeviceOrientationEvent as unknown as DOEStatic).requestPermission === 'function'
  );
}

/** Must be called from within a user gesture (a tap). Resolves the permission state. */
export async function requestGyroPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!gyroExists()) return 'unsupported';
  const doe = window.DeviceOrientationEvent as unknown as DOEStatic;
  if (typeof doe.requestPermission !== 'function') return 'granted'; // Android/desktop: no gate
  try {
    return await doe.requestPermission();
  } catch {
    return 'denied';
  }
}

interface GyroSource extends TiltSource {
  kind: 'gyro';
  /** Resolves true once a real (non-null) orientation reading has arrived. */
  ready(timeoutMs: number): Promise<boolean>;
}

export function createGyroSource(): GyroSource {
  const latest: Orientation = { beta: 0, gamma: 0 };
  let neutral: Orientation = { beta: 0, gamma: 0 };
  let gotData = false;
  const waiters: Array<() => void> = [];

  const onOrient = (e: DeviceOrientationEvent): void => {
    if (e.beta === null || e.gamma === null) return;
    latest.beta = e.beta;
    latest.gamma = e.gamma;
    if (!gotData) {
      gotData = true;
      neutral = { beta: e.beta, gamma: e.gamma };
      while (waiters.length) waiters.shift()!();
    }
  };
  window.addEventListener('deviceorientation', onOrient, true);

  const screenAngle = (): number => {
    const so = (screen as unknown as { orientation?: { angle?: number } }).orientation;
    if (so && typeof so.angle === 'number') return so.angle;
    const legacy = (window as unknown as { orientation?: number }).orientation;
    return typeof legacy === 'number' ? legacy : 0;
  };

  return {
    kind: 'gyro',
    tilt() {
      return tiltFromOrientation(latest, neutral, { screenAngle: screenAngle() });
    },
    recenter() {
      neutral = { beta: latest.beta, gamma: latest.gamma };
    },
    ready(timeoutMs: number) {
      if (gotData) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        const to = setTimeout(() => resolve(false), timeoutMs);
        waiters.push(() => {
          clearTimeout(to);
          resolve(true);
        });
      });
    },
    dispose() {
      window.removeEventListener('deviceorientation', onOrient, true);
    },
  };
}

/* ── touch joystick (fallback) ────────────────────────────────────────────── */

export function createTouchSource(surface: HTMLElement, layer?: HTMLElement, reducedMotion = false): TiltSource {
  let vx = 0;
  let vy = 0;
  const stick: Joystick = createJoystick({
    surface,
    layer,
    reducedMotion,
    onChange: (v) => {
      vx = v.x * v.mag;
      vy = v.y * v.mag;
    },
  });
  return {
    kind: 'touch',
    tilt: () => ({ x: vx, y: vy }),
    recenter() {},
    dispose() {
      stick.destroy();
    },
  };
}

/* ── keyboard (desktop) ───────────────────────────────────────────────────── */

export function createKeySource(): TiltSource {
  const held = new Set<string>();
  const KEYS: Record<string, string> = {
    ArrowUp: 'u',
    ArrowDown: 'd',
    ArrowLeft: 'l',
    ArrowRight: 'r',
    KeyW: 'u',
    KeyS: 'd',
    KeyA: 'l',
    KeyD: 'r',
  };
  const onDown = (e: KeyboardEvent): void => {
    const k = KEYS[e.code];
    if (!k) return;
    held.add(k);
    e.preventDefault();
  };
  const onUp = (e: KeyboardEvent): void => {
    const k = KEYS[e.code];
    if (k) held.delete(k);
  };
  window.addEventListener('keydown', onDown);
  window.addEventListener('keyup', onUp);
  return {
    kind: 'key',
    tilt() {
      const x = (held.has('r') ? 1 : 0) - (held.has('l') ? 1 : 0);
      const y = (held.has('d') ? 1 : 0) - (held.has('u') ? 1 : 0);
      return clampVector(x, y);
    },
    recenter() {
      held.clear();
    },
    dispose() {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    },
  };
}

/* ── a settable source for the browser test hook (synthetic sensor feed) ───── */

export interface SyntheticSource extends TiltSource {
  set(x: number, y: number): void;
}

export function createSyntheticSource(): SyntheticSource {
  let vx = 0;
  let vy = 0;
  return {
    kind: 'key',
    tilt: () => ({ x: vx, y: vy }),
    set(x, y) {
      const v = clampVector(x, y);
      vx = v.x;
      vy = v.y;
    },
    recenter() {},
    dispose() {},
  };
}
